/**
 * Dependency analysis for the mapping DSL.
 *
 * A rule's template / prompt / computed args may reference other OUTPUT cells
 * via `{{out:Field}}`. Those references induce a dependency graph between
 * profile.fields entries. We use it for:
 *   1. topologically ordering rule resolution in buildBaseRow (so an output
 *      ref reads a resolved value instead of "").
 *   2. ordering AI calls in applyAIRules so a Body rule that depends on the
 *      AI-generated Title actually sees it.
 *
 * Independence within a tier is preserved — rules in the same tier run in
 * parallel (in the AI pool), or in profile.executionOrder order if the user
 * has manually prioritised them.
 */

import { templateStrings } from "./modules/catalog";
import { extractFieldRefs } from "@/modules/_shared/templateValue";
import type { Profile, Rule } from "./types";

/**
 * Pull every workbook (output-cell) reference out of a rule's templated values.
 * Returns the unique set of output column names referenced (order is the order
 * seen). Works for both structured TemplateValues and legacy `{{out:Field}}`
 * strings via the shared engine.
 */
export function extractOutputRefs(rule: Rule): string[] {
  const values = templateStrings(rule);
  const refs: string[] = [];
  const seen = new Set<string>();
  for (const v of values) {
    for (const ref of extractFieldRefs(v)) {
      if (ref.origin !== "workbook") continue;
      if (!ref.name || seen.has(ref.name)) continue;
      seen.add(ref.name);
      refs.push(ref.name);
    }
  }
  return refs;
}

// ---------------------------------------------------------------------------
// Topological sort
// ---------------------------------------------------------------------------

export type DependencyAnalysis = {
  /** Columns in resolution order; deps come before their dependents. */
  ordered: string[];
  /** Columns grouped by tier — every column in tier N depends only on tiers <N. */
  tiers: string[][];
  /** column → list of column dependencies. Includes only refs that exist in the profile. */
  depsByColumn: Record<string, string[]>;
  /**
   * Cycles found. Each entry is the cycle path (e.g., ["Title","Body","Title"]).
   * When cycles exist, the affected columns are appended to `ordered` last in
   * arbitrary order so the engine still runs (with refs resolving to "").
   */
  cycles: string[][];
  /** Output refs that point to columns not present in profile.fields. */
  danglingRefs: { column: string; ref: string }[];
};

/**
 * Compute the dependency order for a profile's fields.
 *
 * Kahn's algorithm — ranks each column by the longest dependency chain ending
 * at it. Cycles are detected by leftover columns once Kahn terminates.
 *
 * `executionOrder` is consulted ONLY to break ties within a single tier: the
 * rule appearing earlier in executionOrder runs first. Independent of deps,
 * so users can't override the actual dependency edges (which would break
 * correctness).
 */
export function analyzeDependencies(
  profile: Profile,
  /** Override for sibling order within a tier. Defaults to profile.executionOrder. */
  executionOrderOverride?: readonly string[],
): DependencyAnalysis {
  const fields = profile.fields;
  const columns = Object.keys(fields);
  const executionOrder = executionOrderOverride ?? profile.executionOrder;
  const depsByColumn: Record<string, string[]> = {};
  const danglingRefs: { column: string; ref: string }[] = [];

  // Build edges: dep -> dependents (graph) + indegree (deps count per column).
  const dependents: Record<string, Set<string>> = {};
  const indegree: Record<string, number> = {};
  for (const col of columns) {
    indegree[col] = 0;
    dependents[col] = new Set();
  }

  for (const col of columns) {
    const refs = extractOutputRefs(fields[col]);
    const realDeps: string[] = [];
    for (const ref of refs) {
      if (!(ref in fields)) {
        danglingRefs.push({ column: col, ref });
        continue;
      }
      if (ref === col) {
        // Self-reference treated as a 1-cycle; emit as a degenerate cycle but
        // still record it as a dependency so the cycle detector flags it.
      }
      realDeps.push(ref);
      dependents[ref].add(col);
      indegree[col]++;
    }
    depsByColumn[col] = realDeps;
  }

  // Tier assignment via Kahn's. Pop everything with indegree 0 in the current
  // round → that's tier T. Decrement neighbours; repeat.
  const tiers: string[][] = [];
  const ordered: string[] = [];
  const remaining = new Set(columns);
  const indeg = { ...indegree };

  while (remaining.size > 0) {
    const ready: string[] = [];
    for (const col of remaining) {
      if (indeg[col] === 0) ready.push(col);
    }
    if (ready.length === 0) break; // cycle in the leftover

    // Sort within tier by executionOrder, then alphabetic for determinism.
    ready.sort((a, b) => {
      const ai = executionOrder ? executionOrder.indexOf(a) : -1;
      const bi = executionOrder ? executionOrder.indexOf(b) : -1;
      const aw = ai === -1 ? Number.POSITIVE_INFINITY : ai;
      const bw = bi === -1 ? Number.POSITIVE_INFINITY : bi;
      if (aw !== bw) return aw - bw;
      return a.localeCompare(b);
    });

    tiers.push(ready);
    for (const col of ready) {
      ordered.push(col);
      remaining.delete(col);
      for (const next of dependents[col]) {
        indeg[next]--;
      }
    }
  }

  // Whatever is left is in at least one cycle. Detect the cycles by DFS over
  // the leftover subgraph.
  const cycles: string[][] = [];
  if (remaining.size > 0) {
    const leftover = Array.from(remaining);
    const onPath = new Set<string>();
    const path: string[] = [];

    function dfs(node: string): boolean {
      if (onPath.has(node)) {
        // Found a cycle — slice from where it started.
        const startIdx = path.indexOf(node);
        cycles.push([...path.slice(startIdx), node]);
        return true;
      }
      if (!remaining.has(node)) return false;
      onPath.add(node);
      path.push(node);
      for (const next of dependents[node]) {
        if (dfs(next)) {
          // Don't return early; we still want sibling cycles in big tangles.
        }
      }
      path.pop();
      onPath.delete(node);
      return false;
    }

    for (const node of leftover) dfs(node);

    // Append the leftover columns so applyProfile still emits cells for them
    // (their {{out:...}} refs will resolve to "" — degrades gracefully).
    leftover.sort();
    tiers.push(leftover);
    for (const c of leftover) ordered.push(c);
  }

  return { ordered, tiers, depsByColumn, cycles, danglingRefs };
}

/** Convenience: return only the cycle-error messages a UI can surface. */
export function cycleWarnings(analysis: DependencyAnalysis): string[] {
  return analysis.cycles.map((cycle) => cycleWarning(cycle, analysis));
}

/** Convenience: return dangling-reference messages a UI can surface. */
export function danglingRefWarnings(analysis: DependencyAnalysis): string[] {
  return analysis.danglingRefs.map(
    (d) =>
      `Column "${d.column}" references /out:${d.ref}, but no output column named "${d.ref}" exists in this profile. Pick an existing output column, or use a source field if you meant an uploaded spreadsheet column.`,
  );
}

/** All dependency warnings in the same order the workbook preview shows them. */
export function dependencyWarnings(analysis: DependencyAnalysis): string[] {
  return [...cycleWarnings(analysis), ...danglingRefWarnings(analysis)];
}

function cycleWarning(cycle: string[], analysis: DependencyAnalysis): string {
  const columns = uniqueCycleColumns(cycle);
  if (columns.length === 1) {
    const column = columns[0];
    return [
      `Column "${column}" depends on itself, which creates a loop.`,
      `Its rule reads /out:${column}, so the mapper would need "${column}" before it can calculate "${column}".`,
      `Edit the rule for "${column}" and remove that output reference.`,
      `If you meant the uploaded spreadsheet column named "${column}", use the source field instead of the output field.`,
    ].join(" ");
  }

  const details = cycleReferenceDetails(columns, analysis);
  return [
    `These columns create a loop: ${cycle.join(" → ")}.`,
    details.length > 0 ? `${details.join("; ")}.` : "",
    "The mapper cannot decide which one to calculate first.",
    "Edit one of these rules and remove or replace the output reference.",
  ]
    .filter(Boolean)
    .join(" ");
}

function uniqueCycleColumns(cycle: string[]): string[] {
  const columns: string[] = [];
  for (const column of cycle) {
    if (columns.includes(column)) continue;
    columns.push(column);
  }
  return columns;
}

function cycleReferenceDetails(
  columns: string[],
  analysis: DependencyAnalysis,
): string[] {
  const cycleColumns = new Set(columns);
  const details: string[] = [];
  for (const column of columns) {
    for (const dep of analysis.depsByColumn[column] ?? []) {
      if (!cycleColumns.has(dep)) continue;
      details.push(`Column "${column}" reads /out:${dep}`);
    }
  }
  return details;
}
